import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import { DISPLAY_CHECKBOX_TOGGLED, TOGGLE_DISPLAY_CONFIGURATION_LOADED, TOGGLE_LOAD_PAGE_CONFIGURATION } from "../../../../Context/AliceGPT/AliceGPTContextEvents";
import { useTranslation } from "react-i18next";
import { getEntityMenuAndPageConfiguration, getEntityPagesConfigurations } from "../../../../API/AliceGPT/AliceGPT";
import { useEffect, useState } from "react";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import { Tooltip } from '@progress/kendo-react-tooltip';

function PagesConfiguration({ useAI }) {

    const { t } = useTranslation("create_project/edit_page_configurations");

    const [loading, setLoading] = useState(true);
    const [state, dispatch] = useAliceGPT();

    const selectedEntities = state.entities.filter(entity => entity.checked);

    const enabledColumns = [
        {
            title: t("table"),
            databaseField: "shouldCreateTable",
            disabledField: "isTableDisabled"
        },
        {
            title: t("split_view"),
            databaseField: "shouldCreateSplitView",
            disabledField: ""
        },
        {
            title: t("kanban"),
            databaseField: "shouldCreateKanban",
            disabledField: "isKanbanDisabled"
        },
        {
            title: t("calendar"),
            databaseField: "shouldCreateCalendar",
            disabledField: "isCalendarDisabled"
        },
        {
            title: t("cards"),
            databaseField: "shouldCreateCards",
            disabledField: ""
        },
        {
            title: t("gantt_chart"),
            databaseField: "shouldCreateGanttChart",
            disabledField: "isGanttChartDisabled"
        }
    ];

    useEffect(() => {
        if (state.shouldLoadPageConfiguration) {
            optimizeUsingAI()
                .finally(() => {
                    setLoading(false);
                    dispatch({
                        type: TOGGLE_DISPLAY_CONFIGURATION_LOADED
                    })
                    dispatch({
                        type: TOGGLE_LOAD_PAGE_CONFIGURATION
                    })
                })
        } else {
            if (state.isPageConfigurationLoading) {
                dispatch({
                    type: TOGGLE_DISPLAY_CONFIGURATION_LOADED
                })
            }
            setLoading(false)
        }
    }, []);

    function handleCheckbox(event, entityName) {
        const { name } = event.target;
        dispatch({
            type: DISPLAY_CHECKBOX_TOGGLED,
            payload: {
                entityName,
                name
            }
        })
    }

    async function optimizeUsingAI() {
        try {
            const entities = selectedEntities
                .filter(entity => !entity.isUserEntity)
                .map(entity => ({
                    name: entity.name,
                    attributes: entity.attributes.filter(attribute => attribute.selected).map(attribute => ({
                        name: attribute.name,
                        type: attribute.type
                    }))
                }))
            const response = await getEntityPagesConfigurations(entities);
            for (const element of response) {
                const currentEntityPageConfiguration = selectedEntities.find(entity => entity.name === element.entityName).pageConfiguration;
                const viewsToEnable = element.suitableViews.map(view => `shouldCreate${view.replace(/ /g, "")}`);
                for (const view of enabledColumns) {
                    if (
                        (viewsToEnable.includes(view.databaseField) && !currentEntityPageConfiguration[view.databaseField]) ||
                        (!viewsToEnable.includes(view.databaseField) && currentEntityPageConfiguration[view.databaseField])
                    ) {
                        dispatch({
                            type: DISPLAY_CHECKBOX_TOGGLED,
                            payload: {
                                entityName: element.entityName,
                                name: view.databaseField
                            }
                        })
                    }
                }
            }
        } catch (error) {
            throw new Error("Something went wrong!");
        }
    }

    if (loading) {
        return (
            <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                <img src={getDatalakeImageUrl("loaders/RecommendationLoader.gif")} alt="" />
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        )
    }

    return (
        <>
            {
                selectedEntities.some(entity => Object.values(entity.pageConfiguration).every(value => !value)) ?
                    <div className="text-danger">
                        <i className="fa-solid fa-circle-info"></i>
                        &nbsp;
                        At least one of the selected entities has no page configuration enabled. Please enable at least one page configuration for each entity.
                    </div>
                    :
                    null
            }
            <div className="row mb-3">
                <div className="col-12 mt-2">
                    <Tooltip position='right' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                        <h5 className="d-flex align-items-center" style={{ fontWeight: "700" }}>
                            <span className="font-size-4 font-weight-600 text-1">
                                {t("select_all.label")}&nbsp;
                            </span>
                            <i
                                className="fa-solid fa-circle-info text-2"
                                style={{ opacity: 0.5 }}
                                title={t("select_all.tooltip")}
                            />
                        </h5>
                    </Tooltip>
                </div>
            </div>
            <Grid
                data={selectedEntities}
                className="pages-configuration-grid"
            >
                <GridColumn
                    title={t("objects")}
                    field="name"
                    cell={(props) => {
                        return (
                            <td>
                                {props.dataItem.name}
                                {
                                    Object.values(props.dataItem.pageConfiguration).every(value => !value) ?
                                        <span>
                                            &nbsp;
                                            <i className="fa-solid fa-circle-info text-danger"></i>
                                        </span>
                                        :
                                        null
                                }
                            </td>
                        )
                    }}
                />
                {enabledColumns.map((column, index) => {
                    return (
                        <GridColumn
                            key={index}
                            title={column.title}
                            field={column.databaseField}
                            cell={(props) => {
                                return (
                                    <td>
                                        <Checkbox
                                            aria-label={`${props.dataItem.entityId}-${column.databaseField}`}
                                            name={column.databaseField}
                                            checked={props.dataItem.pageConfiguration[column.databaseField]}
                                            value={props.dataItem.pageConfiguration[column.databaseField]}
                                            disabled={props.dataItem.pageConfiguration[column.disabledField]}
                                            onChange={event => {
                                                handleCheckbox(event, props.dataItem.name)
                                            }}
                                        />
                                    </td>
                                )
                            }}
                        />
                    )
                })}
            </Grid>
        </>
    )
}

export default PagesConfiguration;